import { toBase64 } from '../vault/bytes';

/**
 * The backend's callables over plain fetch. No Firebase SDK: the callable
 * protocol is a POST of `{data}` answering `{result}` or `{error}`, and the
 * head's whole promise is that it ships what it runs.
 *
 * Every vault-shaped callable carries `vaultAuth`. A bare ID token stops
 * being sufficient once a vault is stamped, and this browser is a grantee —
 * it presents the secret it was handed at pairing, never a custodian proof,
 * which it could not compute without the masterKey it deliberately never
 * receives.
 */

export const FUNCTIONS_ORIGIN = 'https://europe-west1-pondering-live.cloudfunctions.net';

export class CallableError extends Error {
  constructor(
    /** The code the backend threw (`permission-denied`), not the canonical name
     *  the wire spells it as (`PERMISSION_DENIED`) — every check here is
     *  written against the former. */
    readonly status: string,
    message: string,
  ) {
    super(message);
    this.name = 'CallableError';
  }

  /** The gate answers every refusal identically — revoked, expired, unknown
   *  and someone else's all collapse into one message so a guessed secret
   *  learns nothing. For this browser they mean the same thing anyway. */
  get needsRepair(): boolean {
    return this.status === 'permission-denied';
  }
}

export interface CallOptions {
  idToken: string;
  /** Base64 of the 32-byte grant secret; omitted on joiner-side pairing
   *  calls, which are authenticated but keyless by definition. */
  grantSecret?: Uint8Array;
  signal?: AbortSignal;
}

export async function callable<T>(
  name: string,
  data: Record<string, unknown>,
  options: CallOptions,
): Promise<T> {
  const body: Record<string, unknown> = { ...data };
  if (options.grantSecret) body['vaultAuth'] = { grantSecret: toBase64(options.grantSecret) };

  const response = await fetch(`${FUNCTIONS_ORIGIN}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.idToken}`,
    },
    body: JSON.stringify({ data: body }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const parsed = (await response.json().catch(() => null)) as
    | { result?: T; error?: { status?: string; message?: string } }
    | null;

  if (!response.ok || parsed?.error) {
    const wire = parsed?.error?.status;
    const status = wire ? wire.toLowerCase().replace(/_/g, '-') : `http-${response.status}`;
    throw new CallableError(status, parsed?.error?.message ?? `${name} failed`);
  }
  return parsed?.result as T;
}
